/**
 * Shared SCM / ops UI strings (EN / TR / AR).
 * Merged into locale packs at i18n init time.
 */
export const scmLocales = {
  en: {
    languages: {
      en: "English",
      tr: "Türkçe",
      ar: "العربية",
      selectLanguage: "Select Language"
    },
    nav: {
      dashboard: "Dashboard",
      home: "Home",
      files: "Files",
      search: "Search",
      settingsTab: "Settings",
      myFiles: "My Files",
      fileDetail: "File Detail",
      uploadFile: "Upload File",
      folderManagement: "Folder Management",
      shareFile: "Share File",
      filePreview: "File Preview",
      versionHistory: "Version History",
      profile: "Profile",
      notifications: "Notifications",
      adminPanel: "Admin Panel",
      privacyPolicy: "Privacy Policy",
      termsOfService: "Terms of Service",
      aiAssistant: "AI Assistant",
      ocrResult: "OCR Result",
      accounting: "Accounting",
      accounts: "Current Accounts",
      exchangeRates: "Exchange Rates",
      approvals: "Approvals",
      inventory: "Warehouse / Stock",
      movements: "Inventory Movements",
      warehouses: "Warehouses",
      goodsReceipt: "Goods Receipt",
      stockTransfer: "Stock Transfer",
      shipments: "Shipment Documents",
      purchaseRequests: "Purchase Requests",
      users: "Users",
      masterData: "Master Data",
      auditLog: "Audit Log",
      suppliers: "Suppliers",
      rfqs: "RFQs",
      purchaseOrders: "Purchase Orders",
      productionPlans: "Production Plans",
      manufacturingOrders: "Manufacturing Orders",
      qualityPlans: "Quality Plans",
      inspections: "Inspections",
      ncr: "NCR / CAPA",
      finishedGoods: "Finished Goods",
      packages: "Packages / Pallets",
      exportOrders: "Export Orders",
      intlShipments: "Intl Shipments",
      containers: "Containers",
      forwarders: "Freight Forwarders",
      customs: "Customs",
      overseasWarehouses: "Overseas Warehouses",
      customers: "Customers",
      priceLists: "Price Lists",
      salesOrders: "Sales Orders",
      fulfillment: "Fulfillment",
      returns: "Returns",
      rma: "RMA",
      warranty: "Warranty",
      serviceOrders: "Service Orders",
      approvalEngine: "Approval Engine",
      notificationEngine: "Notification Engine",
      financeLinks: "Finance Links",
      scmReports: "SCM Reports"
    },
    common: {
      search: "Search…",
      add: "Add",
      create: "Create",
      update: "Update",
      close: "Close",
      open: "Open",
      actions: "Actions",
      status: "Status",
      code: "Code",
      name: "Name",
      notes: "Notes",
      date: "Date",
      quantity: "Quantity",
      amount: "Amount",
      currency: "Currency",
      country: "Country",
      email: "Email",
      phone: "Phone",
      noData: "No data",
      loadError: "Failed to load",
      saveError: "Save failed",
      createError: "Create failed",
      required: "Required",
      all: "All",
      details: "Details",
      refresh: "Refresh",
      seeAll: "See all",
      filter: "Filter"
    },
    dashboard: {
      subtitle: "Operations hub — documents, stock, procurement, production, sales & enterprise",
      fullReports: "Full reports",
      salesOrders: "Sales orders",
      purchaseOrders: "Purchase orders",
      pendingApprovals: "Pending approvals",
      unreadNotifications: "{{count}} unread notifications",
      needsAttention: "Needs attention",
      lowStock: "Low stock",
      approvals: "Approvals",
      openRma: "Open RMA",
      unread: "Unread",
      fulfillment: "Fulfillment",
      goodsReceipt: "Goods receipt",
      mfgOrders: "MFG orders",
      products: "Products",
      export: "Export",
      pipelineStatus: "Pipeline status",
      details: "Details",
      sales: "Sales",
      procurement: "Procurement",
      manufacturing: "Manufacturing",
      noOpenItems: "No open items",
      modules: "Modules",
      modulesHint: "Jump into any area you can access",
      recentActivity: "Recent activity",
      audit: "Audit",
      noAuditYet: "No audit events yet",
      stockEntry: "Stock entry",
      groups: {
        foundation: "Foundation",
        procurement: "Procurement",
        inventory: "Inventory",
        manufacturing: "Manufacturing",
        quality: "Quality & Packaging",
        export: "International",
        sales: "Sales",
        aftersales: "After Sales",
        enterprise: "Enterprise",
        dms: "Documents & AI"
      },
      tiles: {
        users: { title: "Users", description: "Roles & access" },
        masterData: { title: "Master Data", description: "Units, currencies, statuses" },
        auditLog: { title: "Audit Log", description: "Who changed what" },
        suppliers: { title: "Suppliers", description: "Vendor master" },
        rfqs: { title: "RFQs", description: "Request quotes" },
        purchaseOrders: { title: "Purchase Orders", description: "PO lifecycle" },
        products: { title: "Products", description: "Stock master" },
        warehouses: { title: "Warehouses", description: "Locations & balances" },
        goodsReceipt: { title: "Goods Receipt", description: "Receive against PO" },
        stockTransfer: { title: "Stock Transfer", description: "Move between WH" },
        productionPlans: { title: "Production Plans", description: "Plan demand" },
        manufacturingOrders: { title: "Manufacturing Orders", description: "Consume & output" },
        qualityPlans: { title: "Quality Plans", description: "Inspection templates" },
        inspections: { title: "Inspections", description: "Record results" },
        ncr: { title: "NCR / CAPA", description: "Non-conformances" },
        packages: { title: "Packages / Pallets", description: "Pack finished goods" },
        exportOrders: { title: "Export Orders", description: "Export SO pipeline" },
        intlShipments: { title: "Intl Shipments", description: "Forwarding & tracking" },
        containers: { title: "Containers", description: "Load planning" },
        customs: { title: "Customs", description: "Declarations" },
        customers: { title: "Customers", description: "Customer master" },
        priceLists: { title: "Price Lists", description: "Pricing" },
        salesOrders: { title: "Sales Orders", description: "Order to cash" },
        fulfillment: { title: "Fulfillment", description: "Reserve → deliver" },
        returns: { title: "Returns", description: "Customer returns" },
        rma: { title: "RMA", description: "Return authorizations" },
        warranty: { title: "Warranty", description: "Coverage & claims" },
        serviceOrders: { title: "Service Orders", description: "Field / repair" },
        approvals: { title: "Approvals", description: "Workflow inbox" },
        notifications: { title: "Notifications", description: "Event engine" },
        financeLinks: { title: "Finance Links", description: "SO/PO → invoices" },
        scmReports: { title: "SCM Reports", description: "Full KPI board" },
        files: { title: "Files", description: "Document library" },
        aiAssistant: { title: "AI Assistant", description: "Ask your docs" },
        accounting: { title: "Accounting", description: "Invoices & OCR" },
        search: { title: "Search", description: "Find anything" }
      }
    },
    scm: {
      suppliers: {
        title: "Suppliers",
        subtitle: "Procurement supplier master data",
        add: "Add supplier",
        empty: "No suppliers",
        emptyDesc: "Add your first supplier.",
        nameRequired: "Name is required"
      },
      rfqs: {
        title: "RFQs",
        subtitle: "Request for quotation",
        add: "New RFQ",
        empty: "No RFQs"
      },
      purchaseOrders: {
        title: "Purchase Orders",
        subtitle: "PO lifecycle & receipts",
        add: "New PO",
        empty: "No purchase orders"
      },
      warehouses: {
        title: "Warehouses",
        subtitle: "Warehouse & location master",
        add: "Add warehouse",
        empty: "No warehouses",
        detail: "Warehouse detail"
      },
      goodsReceipt: {
        title: "Goods Receipt",
        subtitle: "Receive stock against purchase orders",
        empty: "No goods receipts"
      },
      stockTransfer: {
        title: "Stock Transfer",
        subtitle: "Move stock between warehouses"
      },
      productionPlans: {
        title: "Production Plans",
        subtitle: "Plan manufacturing demand",
        add: "New plan",
        empty: "No production plans"
      },
      manufacturingOrders: {
        title: "Manufacturing Orders",
        subtitle: "Release, consume materials, post output",
        empty: "No manufacturing orders",
        detail: "Manufacturing order"
      },
      qualityPlans: {
        title: "Quality Plans",
        subtitle: "Inspection templates",
        add: "New plan",
        empty: "No quality plans"
      },
      inspections: {
        title: "Inspections",
        subtitle: "Record quality inspection results",
        empty: "No inspections"
      },
      ncr: {
        title: "NCR / CAPA",
        subtitle: "Non-conformances and corrective actions",
        empty: "No NCRs"
      },
      finishedGoods: {
        title: "Finished Goods",
        subtitle: "FG ready for packaging / shipment",
        empty: "No finished goods"
      },
      packages: {
        title: "Packages / Pallets",
        subtitle: "Pack and label finished goods",
        empty: "No packages"
      },
      exportOrders: {
        title: "Export Orders",
        subtitle: "International sales / export pipeline",
        empty: "No export orders"
      },
      intlShipments: {
        title: "International Shipments",
        subtitle: "Forwarding and tracking",
        empty: "No shipments"
      },
      containers: {
        title: "Containers",
        subtitle: "Container load planning",
        empty: "No containers"
      },
      forwarders: {
        title: "Freight Forwarders",
        subtitle: "Logistics partners",
        empty: "No forwarders"
      },
      customs: {
        title: "Customs",
        subtitle: "Declarations and clearance",
        empty: "No customs records"
      },
      overseasWarehouses: {
        title: "Overseas Warehouses",
        subtitle: "Foreign warehouse network",
        empty: "No overseas warehouses"
      },
      customers: {
        title: "Customers",
        subtitle: "Sales customer master",
        add: "Add customer",
        empty: "No customers"
      },
      priceLists: {
        title: "Price Lists",
        subtitle: "Customer and channel pricing",
        empty: "No price lists"
      },
      salesOrders: {
        title: "Sales Orders",
        subtitle: "Order to cash",
        add: "New sales order",
        empty: "No sales orders"
      },
      fulfillment: {
        title: "Fulfillment",
        subtitle: "Reserve, pick, pack, dispatch, deliver"
      },
      returns: {
        title: "Returns",
        subtitle: "Customer returns & restock",
        empty: "No returns"
      },
      rma: {
        title: "RMA",
        subtitle: "Return merchandise authorizations",
        empty: "No RMAs"
      },
      warranty: {
        title: "Warranty",
        subtitle: "Coverage and claims",
        empty: "No warranties"
      },
      serviceOrders: {
        title: "Service Orders",
        subtitle: "Field service / repair",
        empty: "No service orders"
      },
      approvals: {
        title: "Approval Engine",
        subtitle: "Pending SCM approvals",
        empty: "No pending approvals"
      },
      notifications: {
        title: "Notification Engine",
        subtitle: "SCM event notifications",
        empty: "No events"
      },
      financeLinks: {
        title: "Finance Links",
        subtitle: "Link SO/PO to invoices",
        empty: "No finance links"
      },
      reports: {
        title: "SCM Reports",
        subtitle: "KPIs, pipelines, recent audit activity"
      },
      users: {
        title: "Users",
        subtitle: "Foundation directory of users and SCM roles"
      },
      masterData: {
        title: "Master Data",
        subtitle: "Units, currencies, status catalogs"
      },
      audit: {
        title: "Audit Log",
        subtitle: "Immutable change history"
      }
    }
  },
  tr: {
    languages: {
      en: "English",
      tr: "Türkçe",
      ar: "العربية",
      selectLanguage: "Dil Seçin"
    },
    nav: {
      dashboard: "Kontrol Paneli",
      home: "Ana Sayfa",
      files: "Dosyalar",
      search: "Arama",
      settingsTab: "Ayarlar",
      myFiles: "Dosyalarım",
      fileDetail: "Dosya Detayı",
      uploadFile: "Dosya Yükle",
      folderManagement: "Klasör Yönetimi",
      shareFile: "Dosya Paylaş",
      filePreview: "Dosya Önizleme",
      versionHistory: "Sürüm Geçmişi",
      profile: "Profil",
      notifications: "Bildirimler",
      adminPanel: "Yönetici Paneli",
      privacyPolicy: "Gizlilik Politikası",
      termsOfService: "Kullanım Koşulları",
      aiAssistant: "YZ Asistan",
      ocrResult: "OCR Sonucu",
      accounting: "Muhasebe",
      accounts: "Cari Hesaplar",
      exchangeRates: "Kur Tablosu",
      approvals: "Onaylar",
      inventory: "Depo / Stok",
      movements: "Stok Hareketleri",
      warehouses: "Depolar",
      goodsReceipt: "Mal Kabul",
      stockTransfer: "Stok Transferi",
      shipments: "Sevkiyat Belgeleri",
      purchaseRequests: "Satın Alma Talepleri",
      users: "Kullanıcılar",
      masterData: "Ana Veri",
      auditLog: "Denetim Kaydı",
      suppliers: "Tedarikçiler",
      rfqs: "Teklif Talepleri",
      purchaseOrders: "Satın Alma Siparişleri",
      productionPlans: "Üretim Planları",
      manufacturingOrders: "Üretim Emirleri",
      qualityPlans: "Kalite Planları",
      inspections: "Muayeneler",
      ncr: "Uygunsuzluk / DÖF",
      finishedGoods: "Mamuller",
      packages: "Paket / Palet",
      exportOrders: "İhracat Siparişleri",
      intlShipments: "Uluslararası Sevkiyat",
      containers: "Konteynerler",
      forwarders: "Nakliye Firmaları",
      customs: "Gümrük",
      overseasWarehouses: "Yurt Dışı Depolar",
      customers: "Müşteriler",
      priceLists: "Fiyat Listeleri",
      salesOrders: "Satış Siparişleri",
      fulfillment: "Sipariş Karşılama",
      returns: "İadeler",
      rma: "RMA",
      warranty: "Garanti",
      serviceOrders: "Servis Emirleri",
      approvalEngine: "Onay Motoru",
      notificationEngine: "Bildirim Motoru",
      financeLinks: "Finans Bağlantıları",
      scmReports: "SCM Raporları"
    },
    common: {
      search: "Ara…",
      add: "Ekle",
      create: "Oluştur",
      update: "Güncelle",
      close: "Kapat",
      open: "Aç",
      actions: "İşlemler",
      status: "Durum",
      code: "Kod",
      name: "Ad",
      notes: "Notlar",
      date: "Tarih",
      quantity: "Miktar",
      amount: "Tutar",
      currency: "Para birimi",
      country: "Ülke",
      email: "E-posta",
      phone: "Telefon",
      noData: "Veri yok",
      loadError: "Yüklenemedi",
      saveError: "Kaydedilemedi",
      createError: "Oluşturulamadı",
      required: "Zorunlu",
      all: "Tümü",
      details: "Detaylar",
      refresh: "Yenile",
      seeAll: "Tümünü gör",
      filter: "Filtre"
    },
    dashboard: {
      subtitle: "Operasyon merkezi — belgeler, stok, satın alma, üretim, satış ve kurumsal",
      fullReports: "Tüm raporlar",
      salesOrders: "Satış siparişleri",
      purchaseOrders: "Satın alma siparişleri",
      pendingApprovals: "Bekleyen onaylar",
      unreadNotifications: "{{count}} okunmamış bildirim",
      needsAttention: "Dikkat gerektirenler",
      lowStock: "Düşük stok",
      approvals: "Onaylar",
      openRma: "Açık RMA",
      unread: "Okunmamış",
      fulfillment: "Sipariş karşılama",
      goodsReceipt: "Mal kabul",
      mfgOrders: "Üretim emirleri",
      products: "Ürünler",
      export: "İhracat",
      pipelineStatus: "Süreç durumu",
      details: "Detaylar",
      sales: "Satış",
      procurement: "Satın alma",
      manufacturing: "Üretim",
      noOpenItems: "Açık kayıt yok",
      modules: "Modüller",
      modulesHint: "Erişiminiz olan tüm alanlara gidin",
      recentActivity: "Son aktiviteler",
      audit: "Denetim",
      noAuditYet: "Henüz denetim kaydı yok",
      stockEntry: "Stok girişi",
      groups: {
        foundation: "Temel",
        procurement: "Satın Alma",
        inventory: "Envanter",
        manufacturing: "Üretim",
        quality: "Kalite ve Paketleme",
        export: "Uluslararası",
        sales: "Satış",
        aftersales: "Satış Sonrası",
        enterprise: "Kurumsal",
        dms: "Belgeler ve YZ"
      },
      tiles: {
        users: { title: "Kullanıcılar", description: "Roller ve erişim" },
        masterData: { title: "Ana Veri", description: "Birimler, para birimleri, durumlar" },
        auditLog: { title: "Denetim Kaydı", description: "Kim ne değiştirdi" },
        suppliers: { title: "Tedarikçiler", description: "Tedarikçi kartları" },
        rfqs: { title: "Teklif Talepleri", description: "RFQ yönetimi" },
        purchaseOrders: { title: "Satın Alma Siparişleri", description: "PO yaşam döngüsü" },
        products: { title: "Ürünler", description: "Stok kartları" },
        warehouses: { title: "Depolar", description: "Lokasyon ve bakiyeler" },
        goodsReceipt: { title: "Mal Kabul", description: "PO’ya karşı kabul" },
        stockTransfer: { title: "Stok Transferi", description: "Depolar arası hareket" },
        productionPlans: { title: "Üretim Planları", description: "Talep planlama" },
        manufacturingOrders: { title: "Üretim Emirleri", description: "Sarf ve çıktı" },
        qualityPlans: { title: "Kalite Planları", description: "Muayene şablonları" },
        inspections: { title: "Muayeneler", description: "Sonuç kaydı" },
        ncr: { title: "Uygunsuzluk / DÖF", description: "NCR ve düzeltici faaliyet" },
        packages: { title: "Paket / Palet", description: "Mamul paketleme" },
        exportOrders: { title: "İhracat Siparişleri", description: "İhracat süreci" },
        intlShipments: { title: "Uluslararası Sevkiyat", description: "Nakliye ve takip" },
        containers: { title: "Konteynerler", description: "Yük planlama" },
        customs: { title: "Gümrük", description: "Beyannameler" },
        customers: { title: "Müşteriler", description: "Müşteri kartları" },
        priceLists: { title: "Fiyat Listeleri", description: "Fiyatlandırma" },
        salesOrders: { title: "Satış Siparişleri", description: "Siparişten tahsilata" },
        fulfillment: { title: "Sipariş Karşılama", description: "Rezerve → teslim" },
        returns: { title: "İadeler", description: "Müşteri iadeleri" },
        rma: { title: "RMA", description: "İade yetkilendirme" },
        warranty: { title: "Garanti", description: "Kapsam ve talepler" },
        serviceOrders: { title: "Servis Emirleri", description: "Saha / onarım" },
        approvals: { title: "Onaylar", description: "Onay kutusu" },
        notifications: { title: "Bildirimler", description: "Olay motoru" },
        financeLinks: { title: "Finans Bağlantıları", description: "SO/PO → fatura" },
        scmReports: { title: "SCM Raporları", description: "KPI panosu" },
        files: { title: "Dosyalar", description: "Belge kütüphanesi" },
        aiAssistant: { title: "YZ Asistan", description: "Belgelerinize sorun" },
        accounting: { title: "Muhasebe", description: "Faturalar ve OCR" },
        search: { title: "Arama", description: "Her şeyi bulun" }
      }
    },
    scm: {
      suppliers: {
        title: "Tedarikçiler",
        subtitle: "Satın alma tedarikçi ana verisi",
        add: "Tedarikçi ekle",
        empty: "Tedarikçi yok",
        emptyDesc: "İlk tedarikçinizi ekleyin.",
        nameRequired: "Ad zorunludur"
      },
      rfqs: {
        title: "Teklif Talepleri",
        subtitle: "RFQ yönetimi",
        add: "Yeni RFQ",
        empty: "RFQ yok"
      },
      purchaseOrders: {
        title: "Satın Alma Siparişleri",
        subtitle: "PO yaşam döngüsü ve kabuller",
        add: "Yeni PO",
        empty: "Satın alma siparişi yok"
      },
      warehouses: {
        title: "Depolar",
        subtitle: "Depo ve lokasyon ana verisi",
        add: "Depo ekle",
        empty: "Depo yok",
        detail: "Depo detayı"
      },
      goodsReceipt: {
        title: "Mal Kabul",
        subtitle: "Satın alma siparişine karşı stok kabulü",
        empty: "Mal kabul kaydı yok"
      },
      stockTransfer: {
        title: "Stok Transferi",
        subtitle: "Depolar arası stok hareketi"
      },
      productionPlans: {
        title: "Üretim Planları",
        subtitle: "Üretim talebini planlayın",
        add: "Yeni plan",
        empty: "Üretim planı yok"
      },
      manufacturingOrders: {
        title: "Üretim Emirleri",
        subtitle: "Serbest bırak, malzeme sarf et, çıktı kaydet",
        empty: "Üretim emri yok",
        detail: "Üretim emri"
      },
      qualityPlans: {
        title: "Kalite Planları",
        subtitle: "Muayene şablonları",
        add: "Yeni plan",
        empty: "Kalite planı yok"
      },
      inspections: {
        title: "Muayeneler",
        subtitle: "Kalite muayene sonuçlarını kaydedin",
        empty: "Muayene yok"
      },
      ncr: {
        title: "Uygunsuzluk / DÖF",
        subtitle: "Uygunsuzluklar ve düzeltici faaliyetler",
        empty: "Uygunsuzluk kaydı yok"
      },
      finishedGoods: {
        title: "Mamuller",
        subtitle: "Paketleme / sevkiyata hazır mamuller",
        empty: "Mamul yok"
      },
      packages: {
        title: "Paket / Palet",
        subtitle: "Mamul paketleme ve etiketleme",
        empty: "Paket yok"
      },
      exportOrders: {
        title: "İhracat Siparişleri",
        subtitle: "Uluslararası satış / ihracat süreci",
        empty: "İhracat siparişi yok"
      },
      intlShipments: {
        title: "Uluslararası Sevkiyatlar",
        subtitle: "Nakliye ve takip",
        empty: "Sevkiyat yok"
      },
      containers: {
        title: "Konteynerler",
        subtitle: "Konteyner yük planlama",
        empty: "Konteyner yok"
      },
      forwarders: {
        title: "Nakliye Firmaları",
        subtitle: "Lojistik iş ortakları",
        empty: "Nakliye firması yok"
      },
      customs: {
        title: "Gümrük",
        subtitle: "Beyanname ve gümrükleme",
        empty: "Gümrük kaydı yok"
      },
      overseasWarehouses: {
        title: "Yurt Dışı Depolar",
        subtitle: "Yurt dışı depo ağı",
        empty: "Yurt dışı depo yok"
      },
      customers: {
        title: "Müşteriler",
        subtitle: "Satış müşteri ana verisi",
        add: "Müşteri ekle",
        empty: "Müşteri yok"
      },
      priceLists: {
        title: "Fiyat Listeleri",
        subtitle: "Müşteri ve kanal fiyatlandırması",
        empty: "Fiyat listesi yok"
      },
      salesOrders: {
        title: "Satış Siparişleri",
        subtitle: "Siparişten tahsilata",
        add: "Yeni satış siparişi",
        empty: "Satış siparişi yok"
      },
      fulfillment: {
        title: "Sipariş Karşılama",
        subtitle: "Rezerve, topla, paketle, sevk et, teslim et"
      },
      returns: {
        title: "İadeler",
        subtitle: "Müşteri iadeleri ve stoka dönüş",
        empty: "İade yok"
      },
      rma: {
        title: "RMA",
        subtitle: "İade yetkilendirmeleri",
        empty: "RMA yok"
      },
      warranty: {
        title: "Garanti",
        subtitle: "Kapsam ve talepler",
        empty: "Garanti kaydı yok"
      },
      serviceOrders: {
        title: "Servis Emirleri",
        subtitle: "Saha servisi / onarım",
        empty: "Servis emri yok"
      },
      approvals: {
        title: "Onay Motoru",
        subtitle: "Bekleyen SCM onayları",
        empty: "Bekleyen onay yok"
      },
      notifications: {
        title: "Bildirim Motoru",
        subtitle: "SCM olay bildirimleri",
        empty: "Olay yok"
      },
      financeLinks: {
        title: "Finans Bağlantıları",
        subtitle: "SO/PO’yu faturalara bağlayın",
        empty: "Finans bağlantısı yok"
      },
      reports: {
        title: "SCM Raporları",
        subtitle: "KPI’lar, süreçler, son denetim aktiviteleri"
      },
      users: {
        title: "Kullanıcılar",
        subtitle: "Kullanıcılar ve SCM rolleri"
      },
      masterData: {
        title: "Ana Veri",
        subtitle: "Birimler, para birimleri, durum katalogları"
      },
      audit: {
        title: "Denetim Kaydı",
        subtitle: "Değişiklik geçmişi"
      }
    }
  },
  ar: {
    languages: {
      en: "English",
      tr: "Türkçe",
      ar: "العربية",
      selectLanguage: "اختر اللغة"
    },
    nav: {
      dashboard: "لوحة التحكم",
      home: "الرئيسية",
      files: "الملفات",
      search: "بحث",
      settingsTab: "الإعدادات",
      myFiles: "ملفاتي",
      fileDetail: "تفاصيل الملف",
      uploadFile: "رفع ملف",
      folderManagement: "إدارة المجلدات",
      shareFile: "مشاركة ملف",
      filePreview: "معاينة الملف",
      versionHistory: "سجل الإصدارات",
      profile: "الملف الشخصي",
      notifications: "الإشعارات",
      adminPanel: "لوحة الإدارة",
      privacyPolicy: "سياسة الخصوصية",
      termsOfService: "شروط الخدمة",
      aiAssistant: "مساعد الذكاء الاصطناعي",
      ocrResult: "نتيجة OCR",
      accounting: "المحاسبة",
      accounts: "الحسابات الجارية",
      exchangeRates: "أسعار الصرف",
      approvals: "الموافقات",
      inventory: "المستودع / المخزون",
      movements: "حركات المخزون",
      warehouses: "المستودعات",
      goodsReceipt: "استلام البضائع",
      stockTransfer: "تحويل المخزون",
      shipments: "وثائق الشحن",
      purchaseRequests: "طلبات الشراء",
      users: "المستخدمون",
      masterData: "البيانات الرئيسية",
      auditLog: "سجل التدقيق",
      suppliers: "الموردون",
      rfqs: "طلبات التسعير",
      purchaseOrders: "أوامر الشراء",
      productionPlans: "خطط الإنتاج",
      manufacturingOrders: "أوامر التصنيع",
      qualityPlans: "خطط الجودة",
      inspections: "الفحوصات",
      ncr: "عدم المطابقة / الإجراءات التصحيحية",
      finishedGoods: "المنتجات النهائية",
      packages: "الطرود / المنصات",
      exportOrders: "أوامر التصدير",
      intlShipments: "الشحن الدولي",
      containers: "الحاويات",
      forwarders: "وكلاء الشحن",
      customs: "الجمارك",
      overseasWarehouses: "مستودعات خارجية",
      customers: "العملاء",
      priceLists: "قوائم الأسعار",
      salesOrders: "أوامر البيع",
      fulfillment: "تنفيذ الطلبات",
      returns: "المرتجعات",
      rma: "تفويض الإرجاع",
      warranty: "الضمان",
      serviceOrders: "أوامر الخدمة",
      approvalEngine: "محرك الموافقات",
      notificationEngine: "محرك الإشعارات",
      financeLinks: "روابط المالية",
      scmReports: "تقارير سلسلة التوريد"
    },
    common: {
      search: "بحث…",
      add: "إضافة",
      create: "إنشاء",
      update: "تحديث",
      close: "إغلاق",
      open: "فتح",
      actions: "إجراءات",
      status: "الحالة",
      code: "الرمز",
      name: "الاسم",
      notes: "ملاحظات",
      date: "التاريخ",
      quantity: "الكمية",
      amount: "المبلغ",
      currency: "العملة",
      country: "البلد",
      email: "البريد الإلكتروني",
      phone: "الهاتف",
      noData: "لا توجد بيانات",
      loadError: "فشل التحميل",
      saveError: "فشل الحفظ",
      createError: "فشل الإنشاء",
      required: "مطلوب",
      all: "الكل",
      details: "التفاصيل",
      refresh: "تحديث",
      seeAll: "عرض الكل",
      filter: "تصفية"
    },
    dashboard: {
      subtitle: "مركز العمليات — المستندات والمخزون والمشتريات والإنتاج والمبيعات والمؤسسة",
      fullReports: "التقارير الكاملة",
      salesOrders: "أوامر البيع",
      purchaseOrders: "أوامر الشراء",
      pendingApprovals: "الموافقات المعلقة",
      unreadNotifications: "{{count}} إشعار غير مقروء",
      needsAttention: "يحتاج إلى اهتمام",
      lowStock: "مخزون منخفض",
      approvals: "الموافقات",
      openRma: "RMA مفتوح",
      unread: "غير مقروء",
      fulfillment: "تنفيذ الطلبات",
      goodsReceipt: "استلام البضائع",
      mfgOrders: "أوامر التصنيع",
      products: "المنتجات",
      export: "التصدير",
      pipelineStatus: "حالة المسار",
      details: "التفاصيل",
      sales: "المبيعات",
      procurement: "المشتريات",
      manufacturing: "التصنيع",
      noOpenItems: "لا توجد عناصر مفتوحة",
      modules: "الوحدات",
      modulesHint: "انتقل إلى أي منطقة يمكنك الوصول إليها",
      recentActivity: "النشاط الأخير",
      audit: "التدقيق",
      noAuditYet: "لا توجد أحداث تدقيق بعد",
      stockEntry: "إدخال مخزون",
      groups: {
        foundation: "الأساسيات",
        procurement: "المشتريات",
        inventory: "المخزون",
        manufacturing: "التصنيع",
        quality: "الجودة والتعبئة",
        export: "الدولي",
        sales: "المبيعات",
        aftersales: "ما بعد البيع",
        enterprise: "المؤسسة",
        dms: "المستندات والذكاء الاصطناعي"
      },
      tiles: {
        users: { title: "المستخدمون", description: "الأدوار والوصول" },
        masterData: { title: "البيانات الرئيسية", description: "الوحدات والعملات والحالات" },
        auditLog: { title: "سجل التدقيق", description: "من غيّر ماذا" },
        suppliers: { title: "الموردون", description: "بيانات الموردين" },
        rfqs: { title: "طلبات التسعير", description: "إدارة RFQ" },
        purchaseOrders: { title: "أوامر الشراء", description: "دورة أمر الشراء" },
        products: { title: "المنتجات", description: "بيانات المخزون" },
        warehouses: { title: "المستودعات", description: "المواقع والأرصدة" },
        goodsReceipt: { title: "استلام البضائع", description: "الاستلام مقابل أمر الشراء" },
        stockTransfer: { title: "تحويل المخزون", description: "نقل بين المستودعات" },
        productionPlans: { title: "خطط الإنتاج", description: "تخطيط الطلب" },
        manufacturingOrders: { title: "أوامر التصنيع", description: "الاستهلاك والمخرجات" },
        qualityPlans: { title: "خطط الجودة", description: "قوالب الفحص" },
        inspections: { title: "الفحوصات", description: "تسجيل النتائج" },
        ncr: { title: "عدم المطابقة", description: "عدم المطابقة والإجراءات التصحيحية" },
        packages: { title: "الطرود / المنصات", description: "تعبئة المنتجات النهائية" },
        exportOrders: { title: "أوامر التصدير", description: "مسار التصدير" },
        intlShipments: { title: "الشحن الدولي", description: "الشحن والتتبع" },
        containers: { title: "الحاويات", description: "تخطيط التحميل" },
        customs: { title: "الجمارك", description: "الإقرارات" },
        customers: { title: "العملاء", description: "بيانات العملاء" },
        priceLists: { title: "قوائم الأسعار", description: "التسعير" },
        salesOrders: { title: "أوامر البيع", description: "من الطلب إلى التحصيل" },
        fulfillment: { title: "تنفيذ الطلبات", description: "حجز ← تسليم" },
        returns: { title: "المرتجعات", description: "مرتجعات العملاء" },
        rma: { title: "تفويض الإرجاع", description: "تفويضات الإرجاع" },
        warranty: { title: "الضمان", description: "التغطية والمطالبات" },
        serviceOrders: { title: "أوامر الخدمة", description: "خدمة ميدانية / إصلاح" },
        approvals: { title: "الموافقات", description: "صندوق الموافقات" },
        notifications: { title: "الإشعارات", description: "محرك الأحداث" },
        financeLinks: { title: "روابط المالية", description: "أمر بيع/شراء ← فواتير" },
        scmReports: { title: "تقارير سلسلة التوريد", description: "لوحة مؤشرات الأداء" },
        files: { title: "الملفات", description: "مكتبة المستندات" },
        aiAssistant: { title: "مساعد الذكاء الاصطناعي", description: "اسأل مستنداتك" },
        accounting: { title: "المحاسبة", description: "الفواتير وOCR" },
        search: { title: "بحث", description: "ابحث عن أي شيء" }
      }
    },
    scm: {
      suppliers: {
        title: "الموردون",
        subtitle: "بيانات موردي المشتريات",
        add: "إضافة مورد",
        empty: "لا يوجد موردون",
        emptyDesc: "أضف أول مورد.",
        nameRequired: "الاسم مطلوب"
      },
      rfqs: {
        title: "طلبات التسعير",
        subtitle: "إدارة طلبات عرض السعر",
        add: "طلب تسعير جديد",
        empty: "لا توجد طلبات تسعير"
      },
      purchaseOrders: {
        title: "أوامر الشراء",
        subtitle: "دورة أمر الشراء والاستلام",
        add: "أمر شراء جديد",
        empty: "لا توجد أوامر شراء"
      },
      warehouses: {
        title: "المستودعات",
        subtitle: "بيانات المستودعات والمواقع",
        add: "إضافة مستودع",
        empty: "لا توجد مستودعات",
        detail: "تفاصيل المستودع"
      },
      goodsReceipt: {
        title: "استلام البضائع",
        subtitle: "استلام المخزون مقابل أوامر الشراء",
        empty: "لا توجد سجلات استلام"
      },
      stockTransfer: {
        title: "تحويل المخزون",
        subtitle: "نقل المخزون بين المستودعات"
      },
      productionPlans: {
        title: "خطط الإنتاج",
        subtitle: "تخطيط طلب التصنيع",
        add: "خطة جديدة",
        empty: "لا توجد خطط إنتاج"
      },
      manufacturingOrders: {
        title: "أوامر التصنيع",
        subtitle: "الإصدار واستهلاك المواد وتسجيل المخرجات",
        empty: "لا توجد أوامر تصنيع",
        detail: "أمر التصنيع"
      },
      qualityPlans: {
        title: "خطط الجودة",
        subtitle: "قوالب الفحص",
        add: "خطة جديدة",
        empty: "لا توجد خطط جودة"
      },
      inspections: {
        title: "الفحوصات",
        subtitle: "تسجيل نتائج فحص الجودة",
        empty: "لا توجد فحوصات"
      },
      ncr: {
        title: "عدم المطابقة / الإجراءات التصحيحية",
        subtitle: "حالات عدم المطابقة والإجراءات التصحيحية",
        empty: "لا توجد سجلات"
      },
      finishedGoods: {
        title: "المنتجات النهائية",
        subtitle: "جاهزة للتعبئة / الشحن",
        empty: "لا توجد منتجات نهائية"
      },
      packages: {
        title: "الطرود / المنصات",
        subtitle: "تعبئة وتسمية المنتجات النهائية",
        empty: "لا توجد طرود"
      },
      exportOrders: {
        title: "أوامر التصدير",
        subtitle: "مسار المبيعات / التصدير الدولي",
        empty: "لا توجد أوامر تصدير"
      },
      intlShipments: {
        title: "الشحنات الدولية",
        subtitle: "الشحن والتتبع",
        empty: "لا توجد شحنات"
      },
      containers: {
        title: "الحاويات",
        subtitle: "تخطيط تحميل الحاويات",
        empty: "لا توجد حاويات"
      },
      forwarders: {
        title: "وكلاء الشحن",
        subtitle: "شركاء الخدمات اللوجستية",
        empty: "لا يوجد وكلاء شحن"
      },
      customs: {
        title: "الجمارك",
        subtitle: "الإقرارات والتخليص",
        empty: "لا توجد سجلات جمركية"
      },
      overseasWarehouses: {
        title: "المستودعات الخارجية",
        subtitle: "شبكة المستودعات الأجنبية",
        empty: "لا توجد مستودعات خارجية"
      },
      customers: {
        title: "العملاء",
        subtitle: "بيانات عملاء المبيعات",
        add: "إضافة عميل",
        empty: "لا يوجد عملاء"
      },
      priceLists: {
        title: "قوائم الأسعار",
        subtitle: "تسعير العملاء والقنوات",
        empty: "لا توجد قوائم أسعار"
      },
      salesOrders: {
        title: "أوامر البيع",
        subtitle: "من الطلب إلى التحصيل",
        add: "أمر بيع جديد",
        empty: "لا توجد أوامر بيع"
      },
      fulfillment: {
        title: "تنفيذ الطلبات",
        subtitle: "حجز، تجهيز، تعبئة، شحن، تسليم"
      },
      returns: {
        title: "المرتجعات",
        subtitle: "مرتجعات العملاء وإعادة التخزين",
        empty: "لا توجد مرتجعات"
      },
      rma: {
        title: "تفويض الإرجاع",
        subtitle: "تفويضات إرجاع البضائع",
        empty: "لا توجد تفويضات إرجاع"
      },
      warranty: {
        title: "الضمان",
        subtitle: "التغطية والمطالبات",
        empty: "لا توجد سجلات ضمان"
      },
      serviceOrders: {
        title: "أوامر الخدمة",
        subtitle: "خدمة ميدانية / إصلاح",
        empty: "لا توجد أوامر خدمة"
      },
      approvals: {
        title: "محرك الموافقات",
        subtitle: "موافقات سلسلة التوريد المعلقة",
        empty: "لا توجد موافقات معلقة"
      },
      notifications: {
        title: "محرك الإشعارات",
        subtitle: "إشعارات أحداث سلسلة التوريد",
        empty: "لا توجد أحداث"
      },
      financeLinks: {
        title: "روابط المالية",
        subtitle: "ربط أوامر البيع/الشراء بالفواتير",
        empty: "لا توجد روابط مالية"
      },
      reports: {
        title: "تقارير سلسلة التوريد",
        subtitle: "مؤشرات الأداء والمسارات ونشاط التدقيق"
      },
      users: {
        title: "المستخدمون",
        subtitle: "دليل المستخدمين وأدوار سلسلة التوريد"
      },
      masterData: {
        title: "البيانات الرئيسية",
        subtitle: "الوحدات والعملات وكتالوجات الحالة"
      },
      audit: {
        title: "سجل التدقيق",
        subtitle: "سجل التغييرات"
      }
    }
  }
};

export default scmLocales;
